import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { PasswordRequirementsChecklist } from '@/components/auth/PasswordRequirementsChecklist';
import { PasswordStrengthMeter } from '@/components/auth/PasswordStrengthMeter';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { changePassword } from '@/features/auth/api';
import { getAccountReturnTarget } from '@/lib/account-return-target';
import { ApiError, NetworkError } from '@/lib/api-client';
import { isStrongPassword } from '@/lib/password-rules';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

export function PasswordRoute() {
  const navigate = useNavigate();
  const replaceTokens = useAuthStore((s) => s.replaceTokens);
  const user = useAuthStore((s) => s.user);
  // Sebastian ruling 2026-09-15 round 4 — return path depends on the
  // customer's worlds. Book-only lands on /book/meine; audio-only +
  // both-worlds land on /account (their home base).
  const returnTarget = getAccountReturnTarget(user);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [errCurrent, setErrCurrent] = useState<string | null>(null);
  const [errNew, setErrNew] = useState<string | null>(null);
  const [errConfirm, setErrConfirm] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const S = STRINGS.account.password;

  const validateNew = (value: string): string | null => {
    if (!value) return S.errors.required;
    if (!isStrongPassword(value)) return S.errors.weakNewPassword;
    return null;
  };

  const validateConfirm = (value: string, newPw: string): string | null => {
    if (!value) return S.errors.required;
    if (value !== newPw) return S.errors.confirmMismatch;
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const errC = currentPassword ? null : S.errors.required;
    const errN = validateNew(newPassword);
    const errCnf = validateConfirm(confirmPassword, newPassword);

    setErrCurrent(errC);
    setErrNew(errN);
    setErrConfirm(errCnf);

    if (errC || errN || errCnf) return;

    setFormError(null);
    setIsSubmitting(true);
    try {
      const tokens = await changePassword({ currentPassword, newPassword });
      replaceTokens(tokens);
      setSuccessOpen(true);
    } catch (err) {
      if (err instanceof NetworkError) {
        setFormError(S.errors.networkError);
      } else if (err instanceof ApiError && err.httpStatus === 401) {
        // Backend throws UnauthorizedException with the German message
        // — render inline below the current-password field per the
        // existing error-state convention.
        setErrCurrent(S.errors.wrongCurrent);
      } else {
        setFormError(S.errors.changeFailed);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
    navigate(returnTarget.path);
  };

  return (
    <div className="min-h-screen bg-dream-white">
      <ContentContainer maxWidth="narrow">
        <div className="py-6">
          <button
            type="button"
            onClick={() => navigate(returnTarget.path)}
            className="mb-4 text-sm text-dream-navy/70 hover:text-dream-navy transition-colors"
          >
            {returnTarget.label}
          </button>

          <h1 className="text-[22px] font-semibold text-dream-navy mb-2">{S.title}</h1>
          <p className="text-sm text-dream-navy/70 mb-6">{S.infoText}</p>

          {formError && (
            <div
              role="alert"
              className="flex items-start gap-2 mb-4 p-3 rounded-md bg-error/10 border border-error/30 text-error-text text-sm"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{formError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {/* Current password */}
            <div className="space-y-1.5">
              <Label htmlFor="pw-current" className="text-sm text-dream-navy">
                {S.currentLabel}
              </Label>
              <div className="relative">
                <Input
                  id="pw-current"
                  type={showCurrent ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    if (errCurrent) setErrCurrent(null);
                  }}
                  onBlur={() => {
                    if (!currentPassword) setErrCurrent(S.errors.required);
                  }}
                  aria-invalid={Boolean(errCurrent)}
                  className="h-11 bg-dream-white text-dream-navy pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  aria-label={showCurrent ? S.hidePassword : S.showPassword}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-dream-navy/60 hover:text-dream-navy transition-colors bg-dream-white"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errCurrent && (
                <p className="text-xs text-[color:var(--color-error)]">{errCurrent}</p>
              )}
            </div>

            {/* New password */}
            <div className="space-y-1.5">
              <Label htmlFor="pw-new" className="text-sm text-dream-navy">
                {S.newLabel}
              </Label>
              <div className="relative">
                <Input
                  id="pw-new"
                  type={showNew ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (errNew) setErrNew(null);
                  }}
                  onBlur={() => setErrNew(validateNew(newPassword))}
                  aria-invalid={Boolean(errNew)}
                  className="h-11 bg-dream-white text-dream-navy pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  aria-label={showNew ? S.hidePassword : S.showPassword}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-dream-navy/60 hover:text-dream-navy transition-colors bg-dream-white"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errNew && <p className="text-xs text-[color:var(--color-error)]">{errNew}</p>}
              {/* 3-tier strength bar (existing component) + ✓/✗ checklist
                  (new sibling component) — both shown together for the
                  full A-13 visual per Briefing line 514. */}
              <PasswordStrengthMeter password={newPassword} />
              <PasswordRequirementsChecklist password={newPassword} tone="light" />
            </div>

            {/* Confirm new password */}
            <div className="space-y-1.5">
              <Label htmlFor="pw-confirm" className="text-sm text-dream-navy">
                {S.confirmLabel}
              </Label>
              <div className="relative">
                <Input
                  id="pw-confirm"
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errConfirm) setErrConfirm(null);
                  }}
                  onBlur={() => setErrConfirm(validateConfirm(confirmPassword, newPassword))}
                  aria-invalid={Boolean(errConfirm)}
                  className="h-11 bg-dream-white text-dream-navy pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? S.hidePassword : S.showPassword}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-dream-navy/60 hover:text-dream-navy transition-colors bg-dream-white"
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errConfirm && (
                <p className="text-xs text-[color:var(--color-error)]">{errConfirm}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 text-base font-semibold"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting ? S.submitLoading : S.submit}
            </Button>
          </form>
        </div>
      </ContentContainer>

      {/* A-13 Success-Modal (Flag 6 verbatim, single-CTA shadcn Dialog) */}
      <Dialog
        open={successOpen}
        onOpenChange={(open) => {
          if (!open) handleSuccessClose();
        }}
      >
        <DialogContent showCloseButton={false} data-testid="password-change-success-modal">
          <DialogHeader>
            <DialogTitle>{S.successModal.headline}</DialogTitle>
            <DialogDescription>{S.successModal.body}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="m-0 flex flex-col-reverse gap-2 border-0 bg-transparent p-0 sm:flex-row sm:justify-end">
            <Button type="button" onClick={handleSuccessClose}>
              {S.successModal.cta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
